import { useMutation, useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { storiesApi, themesApi, type Story, type StoryGenerationJobResponse } from '@/lib/api-client';
import { useAuthStore } from '@/features/auth/store/authStore';

export function useThemeCategories() {
  return useQuery({
    queryKey: ['theme-categories'],
    queryFn: () => themesApi.getCategories(),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

export function useThemes(options?: { enabled?: boolean; categoryId?: string }) {
  const user = useAuthStore((state) => state.user);

  // Get grade level from user profile or direct grade field
  const gradeLevel = user?.profile?.gradeLevel ?? user?.grade ?? undefined;

  return useQuery({
    queryKey: ['themes', gradeLevel, options?.categoryId],
    queryFn: () => themesApi.getThemes(gradeLevel, options?.categoryId),
    enabled: options?.enabled ?? true,
    staleTime: 30 * 60 * 1000, // 30 minutes - themes rarely change
    gcTime: 60 * 60 * 1000, // 1 hour cache
  });
}

interface GenerateStoryParams {
  themeId: string;
  languageCode?: string;
  asyncGeneration?: boolean;
  forStudentId?: string;
}

export function useGenerateStory() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: ({ themeId, languageCode = 'en-US', asyncGeneration = false, forStudentId }: GenerateStoryParams) => {
      return storiesApi.generateStory({
        theme_id: themeId,
        language_code: languageCode,
        async_generation: asyncGeneration,
        for_student_id: forStudentId,
      });
    },
    onSuccess: (result, variables) => {
      // Handle async generation (returns job response)
      if (variables.asyncGeneration && 'job_id' in result) {
        // Handled by parent component via onSuccess callback
        return;
      }

      // Handle sync generation (returns story)
      const story = result as Story;
      notifications.show({
        title: 'Story Generated!',
        message: `"${story.title}" is ready to read`,
        color: 'teal',
      });
      navigate(`/story/${story.id}`);
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Generation Failed',
        message: error.message,
        color: 'red',
      });
    },
  });
}

export function useStory(storyId: string) {
  return useQuery({
    queryKey: ['story', storyId],
    queryFn: () => storiesApi.getStory(storyId),
    enabled: !!storyId,
  });
}
